import { useEffect, useMemo, useState } from "react";
import { getBrands, deleteBrand, downloadBrandCertificates, updateBrand } from "../../../Api/Network";
import BrandCreateForm from "./BrandCreateForm";
import BrandList from "./BrandList";
import FilterBrand from "./FilterBrand";
import { AiOutlinePlus } from "react-icons/ai";
import PlusIcon from "../../Icons/PlusIcon";
import ViewBrand from "./ViewBrand";
import DeleteIcon from "../IconsCode/DeleteIcon";

function BrandManagment() {
    const [brands, setBrands] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [brandFilters, setBrandFilters] = useState({
        accessOwnBrand: false,
        accessOtherBrands: false,
    });
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [brandError, setBrandError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [viewBrand, setViewBrand] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [brandToDelete, setBrandToDelete] = useState(null);
    const isEmptyBrands = !loadingBrands && !brandError && brands.length === 0;

    const formatPersianDate = (value) => {
        if (value === undefined || value === null || value === "") return "—";
        const parsed = Number(value);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return new Intl.DateTimeFormat("fa-IR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            }).format(new Date(parsed));
        }
        return String(value);
    };

    const fetchBrands = async () => {
        setLoadingBrands(true);
        setBrandError(null);
        try {
            const token = localStorage.getItem("token");
            const data = await getBrands(token);
            if (Array.isArray(data) && data.length > 0) {
                const normalized = data
                    .map((item) => {
                        const createdTime =
                            item.createdTime ?? item.createdAt ?? item.created_at ?? item.creationDate ?? item.created ?? 0;
                        return {
                            id: item.id || item.brandId || item.idValue || Date.now(),
                            name: item.name || item.brandName || item.title || "—",
                            domain: item.domain || item.brandDomain || item.website || item.domainName || "—",
                            isActive:
                                item.isActive !== undefined
                                    ? item.isActive
                                    : item.active !== undefined
                                        ? item.active
                                        : item.status !== undefined
                                            ? item.status === "active" || item.status === 1
                                            : true,
                            language:
                                item.language || item.lang || (item.langType === "ENGLISH" ? "en" : "fa"),
                            accessOwnBrand:
                                item.accessOwnBrand ?? item.policy?.isAccessOwnBrnad ?? item.policy?.isAccessOwnBrand ?? true,
                            accessOtherBrands:
                                item.accessOtherBrands ?? item.policy?.isAccessOtherBrand ?? item.policy?.isAccessOtherBrand ?? false,
                            createdTime,
                            createdAt: formatPersianDate(createdTime),
                            description: item.description || item.desc || item.note || "—",
                        };
                    })
                    .sort((a, b) => (b.createdTime || 0) - (a.createdTime || 0));
                setBrands(normalized);
            }
        } catch (error) {
            console.error("getBrands error", error);
            setBrandError("خطا در بارگذاری برندها. لطفا دوباره تلاش کنید.");
        } finally {
            setLoadingBrands(false);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    const handleCreateBrand = async (payload) => {
        setShowCreateModal(false);
        await fetchBrands();
    };

    const filteredBrands = useMemo(() => {
        let result = brands;

        if (searchTerm.trim()) {
            const normalizedSearch = searchTerm.trim().toLowerCase();
            result = result.filter(
                (brand) =>
                    String(brand.name || "").toLowerCase().includes(normalizedSearch) ||
                    String(brand.domain || "").toLowerCase().includes(normalizedSearch)
            );
        }

        const { accessOwnBrand, accessOtherBrands } = brandFilters;
        if (accessOwnBrand || accessOtherBrands) {
            result = result.filter(
                (brand) =>
                    (accessOwnBrand && brand.accessOwnBrand) ||
                    (accessOtherBrands && brand.accessOtherBrands)
            );
        }

        return result;
    }, [brands, searchTerm, brandFilters]);


    const handleUpdateBrand = async (updatedBrand) => {
        try {
            const token = localStorage.getItem("token");
            await updateBrand(token, updatedBrand);
            await fetchBrands();
            setShowCreateModal(false);
            setEditingBrand(null);
        } catch (error) {
            console.error("Update brand error:", error);
            setBrandError("خطا در ویرایش برند. لطفا دوباره تلاش کنید.");
        }
    };

    const handleEditBrand = (brand) => {
        setEditingBrand(brand);
        setShowCreateModal(true);
    };

    const handleViewBrand = (brand) => {
        setViewBrand(brand);
    };

    const closeViewBrand = () => {
        setViewBrand(null);
    };

    const downloadCertificate = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await downloadBrandCertificates(token, viewBrand.id);

            const base64ToBytes = (base64) => {
                const binary = atob(base64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                return bytes;
            };

            if (!res) {
                console.error("Empty certificate response");
                return;
            }

            // Case: server returned a JSON-typed Blob (application/json)
            if (res instanceof Blob && res.type === "application/json") {
                const text = await res.text();
                let parsed;
                try {
                    parsed = JSON.parse(text);
                } catch (e) {
                    console.error("Failed to parse certificate JSON", e, text);
                    return;
                }
                console.log("certificate JSON:", parsed);

                let bytes;
                if (typeof parsed === "string") {
                    // raw base64 string
                    bytes = base64ToBytes(parsed);
                } else if (parsed.base64 || parsed.data || parsed.file) {
                    bytes = base64ToBytes(parsed.base64 || parsed.data || parsed.file);
                } else if (Array.isArray(parsed)) {
                    bytes = new Uint8Array(parsed);
                } else if (parsed.bytes && Array.isArray(parsed.bytes)) {
                    bytes = new Uint8Array(parsed.bytes);
                } else {
                    console.error("Unknown JSON shape for certificate", parsed);
                    return;
                }

                const zipBlob = new Blob([bytes], { type: "application/zip" });
                const url = window.URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${viewBrand.name?.replace(/[^a-zA-Z0-9-_.]/g, "_") || "certificates"}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            // Case: Blob containing binary data
            if (res instanceof Blob) {
                const ab = await res.arrayBuffer();
                const bytes = new Uint8Array(ab);
                const zipBlob = new Blob([bytes], { type: "application/zip" });
                const url = window.URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${viewBrand.name?.replace(/[^a-zA-Z0-9-_.]/g, "_") || "certificates"}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            // Case: Response-like object or ArrayBuffer/Uint8Array
            if (res && typeof res.arrayBuffer === "function") {
                const ab = await res.arrayBuffer();
                const bytes = new Uint8Array(ab);
                const zipBlob = new Blob([bytes], { type: "application/zip" });
                const url = window.URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${viewBrand.name?.replace(/[^a-zA-Z0-9-_.]/g, "_") || "certificates"}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            if (res instanceof ArrayBuffer) {
                const bytes = new Uint8Array(res);
                const zipBlob = new Blob([bytes], { type: "application/zip" });
                const url = window.URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${viewBrand.name?.replace(/[^a-zA-Z0-9-_.]/g, "_") || "certificates"}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            if (res instanceof Uint8Array) {
                const zipBlob = new Blob([res], { type: "application/zip" });
                const url = window.URL.createObjectURL(zipBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${viewBrand.name?.replace(/[^a-zA-Z0-9-_.]/g, "_") || "certificates"}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            console.error("Unhandled certificate response type:", res);
            return;
        } catch (error) {
            console.error("download certificate error", error);
        }
    };


    const removeBrand = async (id) => {
        try {
            const token = localStorage.getItem("token");
            await deleteBrand(token, id);
            setBrands((prev) => prev.filter((b) => b.id !== id));
        } catch (error) {
            console.error("Error deleting brand:", error);
            setBrandError("خطا در حذف برند. لطفا دوباره تلاش کنید.");
        }
    };

    const handleDeleteBrand = (brand) => {
        setBrandToDelete(brand);
        setShowDeleteModal(true);
    };

    const confirmDeleteBrand = async () => {
        if (brandToDelete) {
            await removeBrand(brandToDelete.id);
            setShowDeleteModal(false);
            setBrandToDelete(null);
        }
    };

    const cancelDeleteBrand = () => {
        setShowDeleteModal(false);
        setBrandToDelete(null);
    };

    return (
        <div className="m-10">
            <div className="flex flex-row-reverse flex-wrap justify-between items-center gap-3 mb-4">
                <h2 className="text-lg font-bold text-gray-800">مدیریت برندها</h2>
                <div className="flex flex-row-reverse items-center gap-3">
                    <FilterBrand
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        filters={brandFilters}
                        onFilterChange={setBrandFilters}
                    />
                    <button
                        onClick={() => {
                            setEditingBrand(null);
                            setShowCreateModal(true);
                        }}
                        className="flex flex-row-reverse items-center py-2 px-8 rounded-lg bg-primary-500 text-white"
                    >
                        <PlusIcon />
                        <p className="mr-2">ایجاد برند</p>
                    </button>
                </div>
            </div>

            {loadingBrands ? (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-blue-700">
                    در حال بارگذاری برندها...
                </div>
            ) : brandError ? (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-center">
                    {brandError}
                </div>
            ) : isEmptyBrands ? (
                <div className="rounded-lg text-center bg-yellow-50 border border-yellow-200 px-4 py-3 text-yellow-700 text-center">
                    هیچ برندی در سیستم ثبت نشده است
                </div>
            ) : filteredBrands.length === 0 ? (
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-gray-700 text-center">
                    نتیجه‌ای مطابق جستجو پیدا نشد
                </div>
            ) : (
                <BrandList
                    brands={filteredBrands}
                    onRemove={handleDeleteBrand}
                    onEdit={handleEditBrand}
                    onView={handleViewBrand}
                />
            )}

            {viewBrand && (
                <ViewBrand
                    viewBrand={viewBrand}
                    closeViewBrand={closeViewBrand}
                    downloadCertificate={downloadCertificate}
                />
            )}

            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[94vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex flex-row-reverse justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-gray-800">
                                    {editingBrand ? "ویرایش برند" : "ایجاد برند جدید"}
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setEditingBrand(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                            <BrandCreateForm
                                key={editingBrand?.id ?? "new"}
                                initialValues={editingBrand}
                                mode={editingBrand ? "edit" : "create"}
                                onSubmit={editingBrand ? handleUpdateBrand : handleCreateBrand}
                                onCancel={() => {
                                    setShowCreateModal(false);
                                    setEditingBrand(null);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && brandToDelete && (
                <>
                    <div className="modal-overlay fixed top-0 left-0 w-screen h-screen bg-black opacity-40" onClick={cancelDeleteBrand}></div>
                    <div className="fixed top-[40%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary-100 rounded-lg py-8 px-16">
                        <div className="flex flex-col">
                            <div className="bg-red-100 rounded-full p-4 flex mx-auto">
                                <DeleteIcon color="#DE0613" size="40" />
                            </div>
                            <p className="mt-2 font-bold rtol text-center">حذف &nbsp; {brandToDelete.name}</p>
                            <p className="rtol mt-4 text-slate-700">آیا از حذف &nbsp; <span className="underline">{brandToDelete.name}</span> &nbsp; <span>{brandToDelete.id}</span>  &nbsp; اطمینان دارید؟ </p>
                            <p className="text-sm mb-4 text-slate-500 text-right">این عمل قابل بازگشت نیست</p>
                            <div className="flex flex-row-reverse justify-between gap-8 mt-6 w-full">
                                <button onClick={confirmDeleteBrand} className="bg-redCustom-500 flex-1 rounded-lg text-white p-2">حذف</button>
                                <button onClick={cancelDeleteBrand} className="border border-primary-500 flex-1 rounded-lg text-primary-500 p-2">لغو</button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default BrandManagment;